import React, { useState, useEffect } from 'react';
import { 
  Container, 
  TextField, 
  Button, 
  Typography, 
  Box,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Chip,
  Divider,
  Stack,
  Checkbox,
  FormGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Card,
  CardContent,
  InputAdornment
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

// Icons
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SendIcon from '@mui/icons-material/Send';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CommentIcon from '@mui/icons-material/Comment';
import Header from '../Header';
import { local_signage, local_url } from '../Const';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const theme = createTheme({
  palette: {
    primary: { 
      main: '#2E7D32',
      light: '#4caf50',
      dark: '#1b5e20' 
    },
    secondary: {
      main: '#f50057',
    },
    background: { 
      default: '#f8f9fa',
      paper: '#ffffff' 
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
          },
        },
        contained: {
          padding: '10px 16px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
  },
});

// Update the channel options array
const channelOptions = [
  "ALL",
  "Website", 
  "In-store"
];

// Create a mapping for the API values
const channelApiMapping = {
  "Website": "WEB",
  "In-store": "POS"
};

export const PromoCode = () => {
  // API URL
  const userId = localStorage.getItem('userId');
  const token = localStorage.getItem('AuthorizationToken');

  // States for excel upload
  const [promoCodeData, setPromoCodeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  
  // States for API data
  const [sfAreas, setSfAreas] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [businessSegments, setBusinessSegments] = useState([]);
  const [businessVerticals, setBusinessVerticals] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [selectedVertical, setSelectedVertical] = useState(null);
  const [channel, setChannel] = useState(null);
  
  // Discount related states
  const [discountOptions, setDiscountOptions] = useState([]);
  const [discountName, setDiscountName] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [validFrom, setValidFrom] = useState(dayjs());
  const [validTo, setValidTo] = useState(dayjs().add(30, 'day'));
  
  // UI states
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Fetch discount options from API
  useEffect(() => {
    fetchDiscountOptions();
  }, []);

  const fetchDiscountOptions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${local_signage}/api/v1/discountlist?DiscountCode=ALL`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error("Failed to fetch discount options");
      
      const data = await response.json();
      setDiscountOptions(data);
      setLoading(false);
    } catch (error) {
      setError("Failed to fetch discount options. Please try again later.");
      setLoading(false);
    }
  };

  // Handle discount selection
  const handleDiscountChange = (event, newValue) => {
    setSelectedDiscount(newValue);
    if (newValue) {
      setDiscountName(newValue.DiscountName);
      setDiscountCode(newValue.DiscountCode);
    } else {
      setDiscountName(null);
      setDiscountCode('');
    }
  };

  // Generate discount code if needed (keeping your existing logic)
  useEffect(() => {
    if (discountName && !discountCode && !selectedDiscount) {
      // Generate a code from the discount name only if manually entered
      const code = discountName
        .replace(/[^a-zA-Z0-9 ]/g, '')
        .replace(/\s+/g, '_')
        .toUpperCase()
        .substring(0, 15);
      
      setDiscountCode(code);
    }
  }, [discountName, discountCode, selectedDiscount]);

  // Add ALL option to businessSegments and businessVerticals
  useEffect(() => {
    fetchSfAreas();
    fetchBusinessSegments();
  }, []);

  // Fetch verticals when segment changes
  useEffect(() => {
    if (selectedSegment && selectedSegment !== "ALL") {
      fetchBusinessVerticals(selectedSegment.businessSegmentCode);
    } else {
      setBusinessVerticals([{
        businessVerticalCode: "ALL",
        businessVerticalName: "ALL"
      }]);
    }
  }, [selectedSegment]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // API functions
  const fetchSfAreas = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${local_url}/api/SFArea/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error("Failed to fetch SF Areas");
      const data = await response.json();
      setSfAreas(data);
    } catch (error) {
      setError("Failed to fetch SF Areas. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessSegments = async () => {
    try {
      const response = await fetch(`${local_url}/api/BizSegments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error("Failed to fetch Business Segments");
      const data = await response.json();
      
      // Add ALL option to business segments
      const allOption = {
        businessSegmentCode: "ALL",
        businessSegmentName: "ALL"
      };
      
      setBusinessSegments([ ...data]);
    } catch (error) {
      setError("Failed to fetch Business Segments. Please try again later.");
    }
  };

  const fetchBusinessVerticals = async (segment) => {
    // If segment is "ALL", fetch without segment
    const url = segment === "ALL" ? `${local_url}/api/BizVertical` : `${local_url}/api/BizVertical/${segment}`;

    if (!segment) {
        setBusinessVerticals([]);
        return;
    }

    try {
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) throw new Error("Failed to fetch Business Verticals");

        const data = await response.json();

        // Add "ALL" option to the list
        const allOption = {
            businessVerticalCode: "ALL",
            businessVerticalName: "ALL"
        };

        setBusinessVerticals([ ...data]);

        // Reset selected vertical when segment changes
        setSelectedVertical(null);
    } catch (error) {
        setError("Failed to fetch Business Verticals. Please try again later.");
        setBusinessVerticals([]);
    }
  };

  const validateDateRange = () => {
    if (!validFrom || !validTo) return false;
    return validFrom.isBefore(validTo);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLoading(true);
      setFileName(file.name);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json(sheet);
          
          // Validate uploaded data
          if (json.length === 0) {
            setError('Uploaded file is empty.');
            setLoading(false);
            return;
          }

          // Check if PromoCode column exists
          if (!json.some(row => 'PromoCode' in row)) {
            setError('PromoCode column not found in the Excel file.');
            setLoading(false);
            return;
          }

          // Check for unique promo codes
          const uniquePromoCodes = new Map();
          const duplicates = [];
          
          for (let row of json) {
            if (!row.PromoCode) {
              continue; // Skip empty rows
            }
            
            const code = row.PromoCode.toString().trim();
            if (uniquePromoCodes.has(code)) {
              duplicates.push(code);
            } else {
              // Store promo code with its usage count if available
              const usageCount = row.Usage ? parseInt(row.Usage) : (row.Usages ? parseInt(row.Usages) : 1);
              uniquePromoCodes.set(code, usageCount);
            }
            if (!json.some(row => 'PromoCode' in row)) {
              setError('PromoCode column not found in the Excel file. Please ensure your Excel has "PromoCode" and "Usage" columns.');
              setLoading(false);
              return;
            }
          }
          
          if (duplicates.length > 0) {
            setError(`Duplicate PromoCode(s) found: ${duplicates.join(', ')}`);
            setLoading(false);
            return;
          }
          
          // Extract valid promo codes with usages
          const validCodesData = Array.from(uniquePromoCodes.entries()).map(([code, usages]) => ({
            promoCode: code,
            usages: usages
          }));
          
          setPromoCodeData(validCodesData);
          
          if (validCodesData.length > 0) {
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
          }
        } catch (err) {
          console.error('Error processing file:', err);
          setError('Error processing file. Please ensure it is a valid Excel file.');
        } finally {
          setLoading(false);
        }
      };
      
      reader.onerror = () => {
        setError('Error reading file.');
        setLoading(false);
      };
      
      reader.readAsArrayBuffer(file);
    }
  };

  const handleAreaChange = (event) => {
    const areaId = event.target.value;
    
    if (event.target.checked) {
      setSelectedAreas([...selectedAreas, areaId]);
    } else {
      setSelectedAreas(selectedAreas.filter(id => id !== areaId));
    }
  };

  const handleSubmit = async () => {
    // Validate form
    if (!discountCode) {
      setError('Discount Code is required.');
      return;
    }
    
    if (!channel) {
      setError('Please select a channel.');
      return;
    }
    
    if (!validateDateRange()) {
      setError('Invalid date range. End date must be after start date.');
      return;
    }
    
    if (selectedAreas.length === 0) {
      setError('Please select at least one SF Area.');
      return;
    }
    
    if (!selectedSegment) {
      setError('Please select a Business Segment.');
      return;
    }
    
    if (!selectedVertical) {
      setError('Please select a Business Vertical.');
      return;
    }
    
    if (promoCodeData.length === 0) {
      setError('Please upload Excel file with PromoCode column.');
      return;
    }
    
    // Submit form
    setLoading(true);
    
    try {
      // Construct the request body
      const requestBody = {
        promoSchemeId: '',
        discountCode: discountCode,
        validFromDate: validFrom.toISOString(),
        validToDate: validTo.toISOString(),
        remarks: remarks,
        closed: false,
        deleted: "N",
        businessSegmentCode: selectedSegment?.businessSegmentCode || "",
        businessVerticalCode: selectedVertical?.businessVerticalCode || "",
        approved: true,
        createdUserID: userId,
        createdDate: new Date().toISOString(),
        dml: "I", // Insert operation
        promo: promoCodeData.map(data => ({
          promoCode: data.promoCode,
          promoSchemeId: '',
          discountCode: discountCode,
          permittedUsageCount: data.usages,
          timesUsed: 0,
          closed: false,
          deleted: "N",
          consumedDate: new Date().toISOString(),
          createdUserID: userId,
          createdDate: new Date().toISOString(),
          dml: "I"
        })),
        channel: (channel === "ALL") 
        ? Object.values(channelApiMapping).map(channelId => ({
            channelId,
            dml: "I"
          }))
        : [{ channelId: channelApiMapping[channel] || channel, dml: "I" }],
        area: selectedAreas.map(areaId => ({
          sfAreaId: areaId.toString(),
          dml: "I"
        }))
      };
      
      // Make the API call
      const response = await fetch(`${local_url}/api/DiscountCouponCode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create discount coupon code');
      }
      
      const data = await response.json();
      console.log('API Response:', data);
      
      // Show success message
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        // Optional: Reset form or redirect
      }, 3000);
      
    } catch (error) {
      console.error('API Error:', error);
      setError(error.message || 'Failed to create discount coupon code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Optional: Add a reset form function if needed
  const resetForm = () => {
    setDiscountName(null);
    setDiscountCode('');
    setRemarks('');
    setValidFrom(dayjs());
    setValidTo(dayjs().add(30, 'day'));
    setSelectedAreas([]);
    setSelectedSegment(null);
    setSelectedVertical(null);
    setChannel(null);
    setPromoCodeData([]);
    setFileName('');
    setSelectedDiscount(null);
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="xl">
        <Header />
        <Paper elevation={3} sx={{ padding: 1, marginTop: 1, borderRadius: 2 }}>
          {/* HEADER SECTION - Discount Name, Business Segment/Vertical, Date Range, Channel */}
          <Paper variant="outlined" sx={{ p: 2, mb: 0 }}>
            <Grid container spacing={2}>
              {/* ROW 1: Discount Name, Discount Code, Segment, Verticals in single line */}
              <Grid container item spacing={2} xs={12}>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    value={selectedDiscount}
                    onChange={handleDiscountChange}
                    options={discountOptions}
                    getOptionLabel={(option) => `${option.DiscountName} (${option.DiscountCode})`}
                    isOptionEqualToValue={(option, value) => option.DiscountCode === value.DiscountCode}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Discount Name & Code" 
                        variant="outlined" 
                        fullWidth
                        required
                      />
                    )}
                    loading={loading && discountOptions.length === 0}
                    loadingText="Loading discount options..."
                  />
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Discount Code"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value)}
                    variant="outlined"
                    fullWidth
                    required
                    disabled={selectedDiscount !== null}
                    inputProps={{ maxLength: 15 }}
                    placeholder="Using selected discount code"
                    InputProps={{
                      style: { 
                        textTransform: 'uppercase',
                        height: '56px'
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    id="business-segment"
                    options={businessSegments}
                    getOptionLabel={(option) => option.businessSegmentName}
                    value={selectedSegment}
                    onChange={(event, newValue) => {
                      setSelectedSegment(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Business Segment"
                        variant="outlined"
                        fullWidth
                        required
                      />
                    )}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "56px" // Fixed height for the input
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    id="business-vertical"
                    options={businessVerticals}
                    getOptionLabel={(option) => option.businessVerticalName}
                    value={selectedVertical}
                    onChange={(event, newValue) => {
                      setSelectedVertical(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Business Vertical"
                        variant="outlined"
                        fullWidth
                        required
                        disabled={!selectedSegment}
                      />
                    )}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "56px" // Fixed height for the input
                      }
                    }}
                  />
                </Grid>
              </Grid>
              
              {/* ROW 2: Date Range, Channel, and Remarks */}
              <Grid container item spacing={2} xs={12} alignItems="center">
                <Grid item xs={12} md={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <DatePicker 
                          label="Valid From" 
                          value={validFrom} 
                          onChange={setValidFrom}
                          slotProps={{ 
                            textField: { 
                              fullWidth: true,
                              required: true,
                              error: validFrom && validTo && validFrom.isAfter(validTo)
                            } 
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <DatePicker 
                          label="Valid To" 
                          value={validTo} 
                          onChange={setValidTo}
                          slotProps={{ 
                            textField: { 
                              fullWidth: true,
                              required: true,
                              error: validFrom && validTo && validFrom.isAfter(validTo)
                            } 
                          }}
                        />
                      </Grid>
                    </Grid>
                  </LocalizationProvider>
                </Grid>
                
                <Grid item xs={12} md={2}>
                  <Autocomplete 
                    options={channelOptions}
                    value={channel}
                    onChange={(e, value) => setChannel(value)}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Channel" 
                        variant="outlined" 
                        required
                      />
                    )}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "56px" // Fixed height for the input
                      }
                    }}
                  />
                </Grid>
                
                {/* Remarks field with nice styling */}
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Remarks"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    variant="outlined"
                    fullWidth
                    placeholder="Add optional notes here"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CommentIcon color="primary" />
                        </InputAdornment>
                      ),
                      style: { height: '56px' }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.light',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: '2px',
                        }
                      }
                    }}
                  />
                </Grid>
                
                {/* Submit Button */}
                <Grid item xs={12} md={3}>
                  <Button 
                    variant="contained" 
                    color="primary"
                    size="large"
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{ 
                      px: 4, 
                      py: 1.5, 
                      height: '56px',
                      width: '100%',
                      fontWeight: 'bold'
                    }}
                  >
                    {loading ? 'Processing...' : 'Create Promocode'}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
          
          {/* MAIN CONTENT - Split SF Areas and Excel Upload */}
          <Grid container spacing={3}>
            {/* Left Side - Areas with Checkboxes */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" color="primary" gutterBottom fontWeight="bold">
                    Select Areas
                  </Typography>
                  
                  <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
                    <FormGroup>
                      <Grid container spacing={1}>
                        {sfAreas.map((area) => (
                          <Grid item xs={12} sm={6} key={area.sfAreaId}>
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  checked={selectedAreas.includes(area.sfAreaId)}
                                  onChange={handleAreaChange}
                                  value={area.sfAreaId}
                                  color="primary"
                                />
                              }
                              label={area.sfAreaName}
                            />
                          </Grid>
                        ))}
                      </Grid>
                      
                    </FormGroup>
                  </FormControl>
                </CardContent>
                {/* Error Alert */}
                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ mt: 3, borderRadius: 2 }}
                    onClose={() => setError('')}
                  >
                    {error}
                  </Alert>
                )}
              </Card>
              
            </Grid>
            
            {/* Right Side - Excel Upload */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ 
                    border: '1px dashed',
                    borderColor: 'primary.light',
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                    backgroundColor: 'background.default',
                    textAlign: 'center',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '30px',
                    maxHeight: '40px'
                  }}>
                  
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={handleFileUpload}
                      style={{ 
                        opacity: 0,
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer',
                        zIndex: 1
                      }} 
                    />
                    
                    <Box sx={{ pointerEvents: 'none' }}>
                      <UploadFileIcon color="primary" sx={{ fontSize: 36, mb: 1 }} />
                      
                      <Typography variant="body2" gutterBottom>
                        {fileName ? 
                          `Selected: ${fileName}` : 
                          'Upload Excel with PromoCode and Usages columns'
                        }
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Table to show uploaded data */}
                  <TableContainer 
                    component={Paper} 
                    variant="outlined" 
                    sx={{ 
                      height: 250,
                      mb: 2,
                      maxHeight: '350px'
                    }}
                  >
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width="10%">#</TableCell>
                          <TableCell>Promo Code</TableCell>
                          <TableCell width="15%">Usages</TableCell>
                          <TableCell width="15%">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {promoCodeData.length > 0 ? (
                          promoCodeData.map((data, index) => (
                            <TableRow key={index} hover>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>{data.promoCode}</TableCell>
                              <TableCell>{data.usages}</TableCell>
                              <TableCell>
                                <Chip 
                                  label="Valid" 
                                  color="success" 
                                  size="small" 
                                  variant="outlined"
                                />
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                              <Typography variant="body2" color="text.secondary">
                                No promo codes uploaded yet
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          
        </Paper>
        
        {/* Success Snackbar */}
        <Snackbar 
          open={success} 
          autoHideDuration={3000} 
          onClose={() => setSuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" variant="filled" sx={{ width: '100%' }}>
            {promoCodeData.length > 0 ? 
              `Promotion created successfully with ${promoCodeData.length} promo codes!` : 
              'Promotion submitted successfully!'}
          </Alert>
        </Snackbar>
      </Container>
    </ThemeProvider>
  );
};

export default PromoCode;